"use client"

import { Link, usePage } from "@inertiajs/react"
import {
  BarChartSquare02,
  Check,
  ChevronDown,
  Copy01,
  File06,
  HomeLine,
  LayoutAlt01,
  Menu02,
  MessageChatCircle,
  PieChart03,
  Rows01,
  SearchLg,
  Settings01,
  Share04,
  X,
} from "@untitledui/icons"
import type { FC, ReactNode } from "react"
import { useRef, useState } from "react"
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Link as AriaLink,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from "react-aria-components"
import { useHotkeys } from "react-hotkeys-hook"

import { cn } from "@/lib/utils"

import { AvatarLabelGroup } from "@uui/base/avatar/avatar-label-group"
import { Badge, BadgeWithDot } from "@uui/base/badges/badges"
import { Button } from "@uui/base/buttons/button"
import { CloseButton } from "@uui/base/buttons/close-button"
import { Input } from "@uui/base/input/input"
import { UntitledLogo } from "@uui/foundations/logo/untitledui-logo"
import { useClipboard } from "@uui/hooks/use-clipboard"

// Types
interface NavSubItem {
  label: string
  href: string
  badge?: ReactNode
}

interface NavItemType {
  label: string
  href?: string
  icon?: FC<{ className?: string }>
  badge?: ReactNode
  items?: NavSubItem[]
}

interface NavItemDividerType {
  divider: true
}

type NavListItem = NavItemType | NavItemDividerType

const SIDEBAR_WIDTH = 292

// UUI Demo Pages - keep minimal to only working routes restored
const uuiDemoPages: NavSubItem[] = [
  { label: "Examples Gallery", href: "/uui-demos/examples" },
]

// Full nav config matching dashboard-08 structure
const navItems: NavListItem[] = [
  { label: "Home", href: "#", icon: HomeLine },
  { label: "Dashboard", href: "#", icon: BarChartSquare02 },
  { label: "Projects", href: "#", icon: Rows01 },
  { divider: true },
  {
    label: "Pages",
    icon: File06,
    items: uuiDemoPages,
  },
  { divider: true },
  { label: "Reporting", href: "#", icon: PieChart03 },
  { label: "Settings", href: "#", icon: Settings01 },
  {
    label: "Support",
    href: "#",
    icon: MessageChatCircle,
    badge: (
      <BadgeWithDot color="success" type="modern" size="sm">
        Online
      </BadgeWithDot>
    ),
  },
  {
    label: "Open in browser",
    href: "https://www.untitledui.com/",
    icon: LayoutAlt01,
  },
]

// Nav Item Base Component
interface NavItemBaseProps {
  href?: string
  icon?: FC<{ className?: string }>
  badge?: ReactNode
  type: "link" | "collapsible" | "collapsible-child"
  current?: boolean
  children?: ReactNode
  onClick?: () => void
}

function NavItemBase({
  href,
  icon: Icon,
  badge,
  type,
  current,
  children,
  onClick,
}: NavItemBaseProps) {
  const baseStyles = cn(
    "group bg-primary outline-focus-ring relative flex w-full cursor-pointer items-center rounded-md transition duration-100 ease-linear select-none",
    "hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2",
    current && "bg-active hover:bg-secondary_hover",
  )

  const iconElement = Icon && (
    <Icon
      aria-hidden="true"
      className="text-fg-quaternary mr-2 size-5 shrink-0 transition-all"
    />
  )

  const badgeElement =
    badge && (typeof badge === "string" || typeof badge === "number") ? (
      <Badge className="ml-3" color="gray" type="pill-color" size="sm">
        {badge}
      </Badge>
    ) : (
      badge
    )

  const labelElement = (
    <span
      className={cn(
        "text-md text-secondary group-hover:text-secondary_hover flex-1 truncate font-semibold transition-all",
        current && "text-secondary_hover",
      )}
    >
      {children}
    </span>
  )

  const isExternal = href && href.startsWith("http")
  const externalIcon = isExternal && (
    <Share04 className="text-fg-quaternary size-4 stroke-[2.5px]" />
  )

  if (type === "collapsible") {
    return (
      <summary className={cn("px-3 py-2", baseStyles)} onClick={onClick}>
        {iconElement}
        {labelElement}
        {badgeElement}
        <ChevronDown
          aria-hidden="true"
          className="text-fg-quaternary ml-3 size-4 shrink-0 stroke-[2.5px] in-open:-scale-y-100"
        />
      </summary>
    )
  }

  if (type === "collapsible-child") {
    if (href && !isExternal) {
      return (
        <Link
          href={href}
          className={cn("py-2 pr-3 pl-10", baseStyles)}
          aria-current={current ? "page" : undefined}
        >
          {labelElement}
          {badgeElement}
        </Link>
      )
    }

    return (
      <AriaLink
        href={href!}
        target={isExternal ? "_blank" : "_self"}
        rel="noopener noreferrer"
        className={cn("py-2 pr-3 pl-10", baseStyles)}
        aria-current={current ? "page" : undefined}
      >
        {labelElement}
        {externalIcon}
        {badgeElement}
      </AriaLink>
    )
  }

  if (href && !isExternal) {
    return (
      <Link
        href={href}
        className={cn("px-3 py-2", baseStyles)}
        aria-current={current ? "page" : undefined}
      >
        {iconElement}
        {labelElement}
        {externalIcon}
        {badgeElement}
      </Link>
    )
  }

  return (
    <AriaLink
      href={href!}
      target={isExternal ? "_blank" : "_self"}
      rel="noopener noreferrer"
      className={cn("px-3 py-2", baseStyles)}
      aria-current={current ? "page" : undefined}
    >
      {iconElement}
      {labelElement}
      {externalIcon}
      {badgeElement}
    </AriaLink>
  )
}

// Nav List Component
interface NavListProps {
  activeUrl?: string
  items: NavListItem[]
  className?: string
}

function NavList({ activeUrl, items, className }: NavListProps) {
  const activeItem = items.find((item) => {
    if ("divider" in item) return false
    return (
      item.href === activeUrl ||
      item.items?.some((subItem) => subItem.href === activeUrl)
    )
  })
  const [openSection, setOpenSection] = useState<string | null>(
    activeItem && "label" in activeItem ? activeItem.label : "Pages",
  )

  const isActive = (href: string) => activeUrl?.startsWith(href) ?? false

  return (
    <ul className={cn("mt-4 flex flex-col px-2 lg:px-4", className)}>
      {items.map((item, index) => {
        if ("divider" in item) {
          return (
            <li key={index} className="w-full px-0.5 py-2">
              <hr className="bg-border-secondary h-px w-full border-none" />
            </li>
          )
        }

        if (item.items?.length) {
          const isOpen = openSection === item.label
          return (
            <details
              key={item.label}
              open={isOpen}
              className="appearance-none py-0.5"
              onToggle={(e) => {
                if (e.currentTarget.open) setOpenSection(item.label)
                else if (openSection === item.label) setOpenSection(null)
              }}
            >
              <NavItemBase
                href={item.href}
                badge={item.badge}
                icon={item.icon}
                type="collapsible"
              >
                {item.label}
              </NavItemBase>

              <dd>
                <ul className="py-0.5">
                  {item.items.map((childItem) => (
                    <li key={childItem.label} className="py-0.5">
                      <NavItemBase
                        href={childItem.href}
                        badge={childItem.badge}
                        type="collapsible-child"
                        current={isActive(childItem.href)}
                      >
                        {childItem.label}
                      </NavItemBase>
                    </li>
                  ))}
                </ul>
              </dd>
            </details>
          )
        }

        return (
          <li key={item.label} className="py-0.5">
            <NavItemBase
              type="link"
              badge={item.badge}
              icon={item.icon}
              href={item.href}
              current={item.href === activeUrl}
            >
              {item.label}
            </NavItemBase>
          </li>
        )
      })}
    </ul>
  )
}

// Account Card Component (simplified)
function NavAccountCard() {
  return (
    <div className="ring-secondary relative flex items-center gap-3 rounded-xl p-3 ring-1 ring-inset">
      <AvatarLabelGroup
        size="md"
        src="https://www.untitledui.com/images/avatars/olivia-rhye?fm=webp&q=80"
        title="Olivia Rhye"
        subtitle="olivia@untitledui.com"
        status="online"
      />
    </div>
  )
}

// Refer a Friend Card Component
function ReferAFriendCard({ onDismiss }: { onDismiss?: () => void }) {
  const { copy, copied } = useClipboard()
  const referralUrl = "uui.com/4060020"

  return (
    <div className="bg-primary ring-secondary flex flex-col rounded-xl p-4 shadow-xs ring-1 ring-inset">
      {onDismiss && (
        <div className="flex items-center justify-between">
          <Badge color="brand" type="pill-color" size="sm">
            Refer friends
          </Badge>
          <CloseButton onClick={onDismiss} size="sm" />
        </div>
      )}
      <div className="mt-3">
        <p className="text-primary text-sm font-semibold">Refer a friend</p>
        <p className="text-tertiary mt-1 text-sm">
          Earn 50% back for 12 months when someone uses your link.
        </p>
      </div>
      <div className="mt-4 flex gap-1">
        <Input
          aria-label="Referral link"
          value={referralUrl}
          isReadOnly
          size="sm"
        />
        <Button
          iconLeading={copied ? Check : Copy01}
          onClick={() => copy(referralUrl)}
          size="md"
          color="tertiary"
        />
      </div>
    </div>
  )
}

// Sidebar Content Component
function SidebarContent() {
  const { url } = usePage()
  const searchInputRef = useRef<HTMLInputElement>(null)

  useHotkeys(
    "meta+k",
    (e) => {
      e.preventDefault()
      searchInputRef.current?.focus()
    },
    { enableOnFormTags: true },
  )

  return (
    <aside
      style={{ "--width": `${SIDEBAR_WIDTH}px` } as React.CSSProperties}
      className="border-secondary bg-primary flex h-full w-full max-w-full flex-col justify-between overflow-auto pt-4 md:border-r lg:w-(--width) lg:pt-6"
    >
      {/* Logo & Search */}
      <div className="flex flex-col gap-5 px-4 lg:px-5">
        <UntitledLogo className="h-8" />
        <Input
          ref={searchInputRef}
          shortcut
          size="sm"
          aria-label="Search"
          placeholder="Search"
          icon={SearchLg}
        />
      </div>

      {/* Navigation */}
      <NavList activeUrl={url} items={navItems} className="mt-5 flex-1" />

      {/* Footer */}
      <div className="mt-auto flex flex-col gap-4 px-2 py-4 lg:px-4 lg:py-6">
        <ReferAFriendCard />
        <NavAccountCard />
      </div>
    </aside>
  )
}

// Main DemoSidebar Component
export function DemoSidebar() {
  return (
    <>
      {/* Mobile header with hamburger menu */}
      <AriaDialogTrigger>
        <header className="border-secondary bg-primary flex h-16 items-center justify-between border-b py-3 pr-2 pl-4 lg:hidden">
          <UntitledLogo />

          <AriaButton
            aria-label="Expand navigation menu"
            className="group bg-primary text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover flex items-center justify-center rounded-lg p-2 focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            <Menu02 className="size-6 transition duration-200 ease-in-out group-aria-expanded:opacity-0" />
            <X className="absolute size-6 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100" />
          </AriaButton>
        </header>

        <AriaModalOverlay
          isDismissable
          className={({ isEntering, isExiting }) =>
            cn(
              "bg-overlay/70 fixed inset-0 z-50 cursor-pointer pr-16 backdrop-blur-md lg:hidden",
              isEntering && "animate-in fade-in duration-300 ease-in-out",
              isExiting && "animate-out fade-out duration-200 ease-in-out",
            )
          }
        >
          {({ state }) => (
            <>
              <AriaButton
                aria-label="Close navigation menu"
                onPress={() => state.close()}
                className="text-fg-white/70 outline-focus-ring hover:text-fg-white fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2"
              >
                <X className="size-6" />
              </AriaButton>

              <AriaModal className="w-full cursor-auto will-change-transform">
                <AriaDialog className="h-dvh outline-hidden focus:outline-hidden">
                  <SidebarContent />
                </AriaDialog>
              </AriaModal>
            </>
          )}
        </AriaModalOverlay>
      </AriaDialogTrigger>

      {/* Desktop sidebar (fixed) */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex">
        <SidebarContent />
      </div>

      {/* Placeholder for fixed sidebar width */}
      <div
        style={{ paddingLeft: SIDEBAR_WIDTH }}
        className="invisible hidden lg:sticky lg:top-0 lg:bottom-0 lg:left-0 lg:block"
      />
    </>
  )
}
